import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Worker, Viewer } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import { apigetHomePyqData, apigetPyqPdf } from "../../services/apihomePyqs/apihomepyq";
import apiurl from "../../services/apiendpoint/apiendpoint";
import { FileText, Video, X } from "lucide-react";

export default function PyqPapersPage() {
    const { slug, sectionId } = useParams();    
    const navigate = useNavigate();

    const [pyqData, setPyqData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedFile, setSelectedFile] = useState(null);
    const [selectedFileIndex, setSelectedFileIndex] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [error, setError] = useState(null);
    const [pdfUrls, setPdfUrls] = useState({});

    const defaultLayoutPluginInstance = defaultLayoutPlugin({
        sidebarTabs: () => [],
        toolbarPlugin: {
            fullScreenPlugin: {
                onEnterFullScreen: (zoom) => {
                    zoom(1);
                },
            },
            getFilePlugin: {
                fileNameGenerator: () => "",
            },
            printPlugin: {
                enableShortcuts: false,
            },
            downloadPlugin: {
                enableShortcuts: false,
            },
        },
        renderToolbar: (Toolbar) => (
            <Toolbar>
                {(slots) => {
                    const {
                        CurrentPageInput,
                        GoToNextPage,
                        GoToPreviousPage,
                        NumberOfPages,
                        ZoomIn,
                        ZoomOut,
                        Zoom,
                        EnterFullScreen,
                    } = slots;
                    return (
                        <div style={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <GoToPreviousPage />
                                <CurrentPageInput /> / <NumberOfPages />
                                <GoToNextPage />
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <ZoomOut />
                                <Zoom />
                                <ZoomIn />
                                <EnterFullScreen />
                            </div>
                        </div>
                    );
                }}
            </Toolbar>
        ),
    });

    // useEffect(() => {
    //     if (isModalOpen && selectedFile?.file_type === 'pdf') {
    //         const timer = setTimeout(() => {
    //             const fullscreenBtn = document.querySelector('[data-testid="full-screen__enter-button"]');
    //             if (fullscreenBtn) {
    //                 fullscreenBtn.click();
    //             }
    //         }, 500);
            
    //         return () => clearTimeout(timer);
    //     }
    // }, [isModalOpen, selectedFile]);

     useEffect(() => {
        const disableTextSelection = () => {
            document.addEventListener('selectstart', (e) => e.preventDefault());
        };

        const disableRightClick = () => {
            document.addEventListener('contextmenu', (e) => e.preventDefault());
        };

        const disableCopyPaste = (e) => {
            if (e.ctrlKey && (e.key === 'c' || e.key === 'p')) {
                e.preventDefault();
            }
        };

        disableTextSelection();
        disableRightClick();
        document.addEventListener('keydown', disableCopyPaste);

        return () => {
            document.removeEventListener('selectstart', disableTextSelection);
            document.removeEventListener('contextmenu', disableRightClick);
            document.removeEventListener('keydown', disableCopyPaste);
        };
    }, []);

    useEffect(() => {
        const fetchPyqPapers = async () => {
            try {
                setLoading(true);
                setError(null);
                const res = await apigetHomePyqData(slug, sectionId);
                
                if (res?.response) {
                    const { title, section, pyqId } = res.response;
                    
                    if (!section) {
                        setError('Section not found');
                        setPyqData(null);
                    } else {
                        setPyqData({Title: title,section: section,pyqId: pyqId,sectionId: sectionId });
                    }
                } else {
                    setError('Invalid data format');
                    setPyqData(null);
                }
            } catch (err) {
                setError(err.message || 'Failed to load data');
                setPyqData(null);
            } finally {
                setLoading(false);
            }
        };
        
        fetchPyqPapers();
    }, [slug, sectionId]);

    const handleFileView = (file, index) => {
        setSelectedFile(file);
        setSelectedFileIndex(index);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedFile(null);
        setSelectedFileIndex(null);
    };

    const handleBackClick = () => {
        navigate(`/pyq-details/${slug}`);
    };

    useEffect(() => {
        const loadPdfUrls = async () => {
            if (!pyqData?.section?.media_files) return;
            
            const urls = {};
            for (let i = 0; i < pyqData.section.media_files.length; i++) {
                const file = pyqData.section.media_files[i];
                if (file.file_type === 'pdf') {
                    try {
                        const blob = await apigetPyqPdf(pyqData.pyqId, pyqData.sectionId, i);
                        urls[i] = URL.createObjectURL(blob);
                    } catch (error) {
                        console.error('Error fetching PDF:', error);
                    }
                }
            }
            setPdfUrls(urls);
        };
        
        if (pyqData) {
            loadPdfUrls();
        }
        
        return () => {
            Object.values(pdfUrls).forEach(url => URL.revokeObjectURL(url));
        };
    }, [pyqData]);

    const getFullFileUrl = (fileUrl) => {
        if (!fileUrl) return '';
        if (fileUrl.startsWith('http://') || fileUrl.startsWith('https://')) {
            return fileUrl;
        }
        return `${apiurl()}/${fileUrl}`;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-4 border-4 rounded-full border-regal-blue border-t-transparent animate-spin"></div>
                    <p className="text-gray-600">Loading papers...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <p className="mb-4 text-lg text-red-600">Error: {error}</p>
                    <button onClick={handleBackClick} className="px-4 py-2 text-white transition-colors rounded-lg bg-regal-blue hover:bg-opacity-90">Go Back</button>
                </div>
            </div>
        );
    }

    if (!pyqData || !pyqData.section) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <p className="mb-4 text-lg text-gray-600">No data found for this section</p>
                    <button onClick={handleBackClick} className="px-4 py-2 text-white transition-colors rounded-lg bg-regal-blue hover:bg-opacity-90">
                        Go Back
                    </button>
                </div>
            </div>
        );
    }

    return (
        <>
            <section className="bg-regal-blue">
                <div className="max-w-[85rem] mx-auto px-4 py-8">
                    <button  onClick={handleBackClick} className="flex items-center gap-2 mb-4 text-white transition-colors hover:text-regal-yellow">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        <span>Back to Overview</span>
                    </button>
                    
                    <div className="text-white">
                        <h1 className="mb-2 text-3xl font-bold md:text-4xl">{pyqData.Title}</h1>
                        <h2 className="text-2xl font-semibold text-regal-yellow">{pyqData.section.sectionTitle}</h2>
                    </div>
                </div>
            </section>

            <section className="max-w-[85rem] mx-auto px-4 py-12">
                <div className="mb-6">
                    <p className="text-gray-600">
                        Click on any paper to view it in full screen
                    </p>
                </div>

                {pyqData.section.media_files && pyqData.section.media_files.length > 0 ? (
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                        {pyqData.section.media_files.map((file, index) => (
                            <div key={file._id}
                                className="overflow-hidden transition-all duration-300 bg-white border border-gray-200 shadow-lg cursor-pointer rounded-xl group"
                                onClick={() => handleFileView(file, index)}
                            >
                                <div className="flex items-center justify-center p-8 bg-[#EA5544]">
                                    {file.file_type === 'pdf' ? (
                                        <div className="text-white ">
                                            <FileText className="" />
                                        </div>
                                    ) : (
                                        <Video className="text-white " />
                                    )}
                                </div>

                                <div className="p-4">
                                    <h3 className="mb-2 text-lg font-bold text-gray-800 transition-colors group-hover:text-regal-blue">
                                        {file.title}
                                    </h3>
                                    <div className="flex items-center justify-between">
                                        <span className="px-3 py-1 text-xs font-semibold text-gray-500 uppercase bg-gray-100 rounded-full">
                                            {file.file_type}
                                        </span>
                                        <svg className="w-6 h-6 transition-transform text-regal-blue group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="py-16 text-center">
                        <svg className="w-24 h-24 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <p className="text-xl text-gray-500">No files available in this section</p>
                    </div>
                )}
            </section>

            {isModalOpen && selectedFile && selectedFileIndex !== null && (
                <div className="fixed inset-0 z-[70] overflow-hidden">
                    <div className="absolute inset-0 transition-opacity bg-black bg-opacity-75" onClick={closeModal}></div>

                    <div className="absolute inset-0 flex items-center justify-center p-4">
                        <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-7xl h-[90vh] flex flex-col">
                            <div className="flex items-center justify-between p-4 border-b bg-regal-blue rounded-t-xl">
                                <div>
                                    <h3 className="text-xl font-bold text-white">{selectedFile.title}</h3>
                                    <p className="text-sm text-regal-yellow">{pyqData.section.sectionTitle}</p>
                                </div>
                                <button onClick={closeModal} className="p-1 text-white rounded-full hover:bg-gray-200 hover:text-regal-yellow">
                                  <X/>
                                </button>
                            </div>

                            <div className="flex-1 p-4 overflow-hidden">
                                {selectedFile.file_type === 'pdf' ? (
                                    <div className="h-full">
                                        <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                                           <Viewer fileUrl={pdfUrls[selectedFileIndex]} plugins={[defaultLayoutPluginInstance]}/>
                                        </Worker>
                                    </div>
                                ) : selectedFile.file_type === 'video' ? (
                                    <div className="flex items-center justify-center h-full">
                                        <iframe src={getFullFileUrl(selectedFile.file_url)} className="w-full h-full rounded-lg" allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                            allowFullScreen></iframe>
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-center h-full text-gray-500">
                                        <p>Unsupported file type</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}